import type { ComponentType, ReactNode } from 'react'
import type { LucideProps } from 'lucide-react'

import { cx } from './cx'

/*
 |-----------------------------------------------------------------------------
 | Cargando · vacío · error
 |-----------------------------------------------------------------------------
 | Hoy los tres son la misma línea repetida nueve veces:
 |
 |     <p className="text-sm text-slate-500">Cargando…</p>
 |
 | Y son la primera impresión del producto: con 80 ms de ida y vuelta hasta
 | Lima, esa frase gris es literalmente lo que el cliente ve mientras espera su
 | galería. De todo el rediseño, esto es lo que más cambia la percepción por
 | hora invertida.
 */

/* -------------------------------------------------------------------------- */
/* Cargando                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Bloque de carga con la FORMA de lo que va a aparecer.
 *
 * Un esqueleto que no coincide con el resultado es peor que nada: la página
 * salta al llegar los datos y en móvil eso significa tocar el sitio
 * equivocado. Por eso los esqueletos de abajo están dimensionados contra su
 * componente real, no «a ojo».
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cx('animate-pulse rounded-control bg-surface-sunken', className)}
    />
  )
}

/**
 * Esqueleto de la retícula del portal.
 *
 * `aspect-square` y las mismas columnas que `GalleryPage`: al llegar las fotos
 * ocupan exactamente el hueco que ya estaba reservado y nada se mueve.
 */
export function GallerySkeleton({ count = 12 }: { count?: number }) {
  return (
    <div
      // El estado de carga se anuncia una vez, no una por celda.
      role="status"
      aria-label="Cargando las fotos"
      className="grid grid-cols-2 gap-1.5 px-1.5 xs:gap-2 xs:px-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    >
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className="aspect-square rounded-card" />
      ))}
    </div>
  )
}

/** Esqueleto de una lista de tarjetas: proyectos, planes. */
export function CardsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div role="status" aria-label="Cargando" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className="h-28 rounded-card" />
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Vacío y error                                                               */
/* -------------------------------------------------------------------------- */

interface StateProps {
  icon?: ComponentType<LucideProps>
  title: string
  /** Qué ha pasado y, si se puede, qué hacer. */
  description?: string
  action?: ReactNode
  className?: string
}

/**
 * Estado vacío.
 *
 * Siempre con salida: «Aún no hay proyectos» a secas deja al usuario mirando.
 * El estado vacío de una lista es el mejor sitio del producto para enseñar cómo
 * se empieza, porque es el único momento en que se tiene toda su atención.
 */
export function EmptyState({ icon: Icon, title, description, action, className }: StateProps) {
  return (
    <div className={cx('flex flex-col items-center px-6 py-14 text-center', className)}>
      {Icon !== undefined && (
        <span className="mb-4 grid h-12 w-12 place-items-center rounded-full bg-surface-sunken text-ink-subtle">
          <Icon size={22} strokeWidth={1.75} aria-hidden />
        </span>
      )}

      <p className="text-subhead font-semibold text-ink">{title}</p>

      {description !== undefined && (
        <p className="mt-1.5 max-w-prose text-body text-ink-muted">{description}</p>
      )}

      {action !== undefined && <div className="mt-5">{action}</div>}
    </div>
  )
}

/**
 * Error de carga, siempre con reintento.
 *
 * En Perú la causa más común no es un fallo del servidor sino una conexión que
 * se cayó un segundo. «No se pudieron cargar los proyectos» sin un botón obliga
 * a recargar la página entera; con él, se resuelve en un toque.
 */
export function ErrorState({ icon: Icon, title, description, action, className }: StateProps) {
  return (
    <div role="alert" className={cx('flex flex-col items-center px-6 py-14 text-center', className)}>
      {Icon !== undefined && (
        <span className="mb-4 grid h-12 w-12 place-items-center rounded-full bg-danger-subtle text-danger">
          <Icon size={22} strokeWidth={1.75} aria-hidden />
        </span>
      )}

      <p className="text-subhead font-semibold text-ink">{title}</p>

      {description !== undefined && (
        <p className="mt-1.5 max-w-prose text-body text-ink-muted">{description}</p>
      )}

      {action !== undefined && <div className="mt-5">{action}</div>}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Progreso                                                                    */
/* -------------------------------------------------------------------------- */

interface ProgressProps {
  /** De 0 a 100. */
  value: number
  label: string
  /** Cambia de color al acercarse al límite. Para la cuota, no para subidas. */
  warnAt?: number
  dangerAt?: number
  className?: string
}

export function Progress({ value, label, warnAt, dangerAt, className }: ProgressProps) {
  const percent = Math.max(0, Math.min(100, value))

  const tone =
    dangerAt !== undefined && percent >= dangerAt
      ? 'bg-danger'
      : warnAt !== undefined && percent >= warnAt
        ? 'bg-warning'
        : 'bg-accent'

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      // `track` y no `surface-sunken`: con el matiz de la marca, una barra al
      // 0 % se lee como llena de un color claro en vez de vacía.
      className={cx('h-2 overflow-hidden rounded-full bg-track', className)}
    >
      <div
        className={cx('h-full rounded-full transition-[width] duration-300', tone)}
        style={{ width: `${percent}%` }}
      />
    </div>
  )
}
